<template>
    <validation-observer v-if="!loading" v-slot="{ validate }" slim>
        <form @submit.prevent="store(validate)" class="mb-4 p-2">
            <div class="row">
                <div class="col card p-3 mx-2">
                    <h4>{{ $t('Vehicle') }}</h4>
                    <app-select
                        id="vehicle_id"
                        v-model="form.vehicle_id"
                        :options="vehicles.map(c => c.id)"
                        rules="required"
                        name="vehicle_id"
                        openDirection="bottom"
                        searchable
                        not-empty
                        closeOnSelect
                        @input="id => getPrice(id)"
                        :custom-label="id => getVehicleName(id)">
                        <template #label>
                            {{ $t('Vehicle') }}*
                        </template>
                    </app-select>
                    <app-overlapping :vehicle="vehicle"/>
                    <app-datepicker id="search_dates"
                                    v-model="search_dates"
                                    range
                                    name="search_dates"
                                    class="mb-1"
                                    @change="getPrice(form.vehicle_id)"
                                    rules="required">
                        <template #label>
                            {{ $t('Start date') }} & {{ $t('Return date') }}*
                        </template>
                    </app-datepicker>
                    <div class="row">
                        <div class="col">
                            <app-datepicker id="start_time"
                                            v-model="start_time"
                                            inputType="time"
                                            name="start_time"
                                            class="mb-1"
                                            @change="getPrice(form.vehicle_id)"
                                            rules="required">
                                <template #label>
                                    {{ $t('Start time') }}*
                                </template>
                            </app-datepicker>
                        </div>
                        <div class="col">
                            <app-datepicker id="end_time"
                                            v-model="end_time"
                                            inputType="time"
                                            name="end_time"
                                            class="mb-1"
                                            @change="getPrice(form.vehicle_id)"
                                            rules="required">
                                <template #label>
                                    {{ $t('Return time') }}*
                                </template>
                            </app-datepicker>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <app-select
                                id="pickup_location_id"
                                v-model="form.pickup_location_id"
                                :options="locations.map(c => c.id)"
                                rules="required"
                                name="pickup_location_id"
                                openDirection="bottom"
                                searchable
                                not-empty
                                closeOnSelect
                                @input="checkPaidServices"
                                :custom-label="
                                    id =>
                                        locations.some(c => c.id === id)
                                            ? locations.find(c => c.id === id).name[locale]
                                            : null
                                ">
                                <template #label>
                                    {{ $t('Pick up from') }}*
                                </template>
                            </app-select>
                            <app-input v-if="form.pickup_location_id === otherLocationId"
                                       class="mt-2"
                                       id="pickup_location"
                                       v-model="form.pickup_location_custom"
                                       :placeholder="`${$t('Address')}*`"
                                       @blur="checkPaidServices"
                                       name="pickup_location"
                                       rules="required" />
                        </div>
                        <div class="col">
                            <app-select
                                id="return_location_id"
                                v-model="form.return_location_id"
                                :options="locations.map(c => c.id)"
                                rules="required"
                                name="return_location_id"
                                openDirection="bottom"
                                searchable
                                not-empty
                                closeOnSelect
                                @input="checkPaidServices"
                                :custom-label="id => locations.some(c => c.id === id) ? locations.find(c => c.id === id).name[locale] : null">
                                <template #label>
                                    {{ $t('Return to') }}*
                                </template>
                            </app-select>
                            <app-input v-if="form.return_location_id === otherLocationId"
                                       class="mt-2"
                                       id="return_location"
                                       v-model="form.return_location_custom"
                                       :placeholder="`${$t('Address')}*`"
                                       @blur="checkPaidServices"
                                       name="return_location"
                                       rules="required" />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <app-textarea id="notes" v-model="form.notes" name="notes" :rows="3">
                                <template #label>
                                    {{ $t('Internal notes') }}
                                </template>
                            </app-textarea>
                        </div>
                    </div>
                    <div class="mt-2 d-flex justify-content-start">
                        <app-custom-checkbox id="travel_out_check" v-model="travel_out_check" name="travel_out_check">
                            {{ $t('Travel out of Estonia') }}
                        </app-custom-checkbox>
                    </div>
                    <app-input v-if="travel_out_check" id="travel_out" v-model="form.travel_out" name="travel_out" rules="required" class="mt-2">
                        <template #label> {{ $t('Countries') }}*</template>
                    </app-input>
                    <div class="row mt-2">
                        <div class="col-md-6 col-sm-12">
                            <div class="accordion" id="ad_eqs">
                                <div class="card shadow-none">
                                    <div class="card-header pointer accordeon-border" data-toggle="collapse" href="#additional_equipment">
                                        <a class="card-link font-weight-normal pl-1">
                                            {{ $t('Additional eq') }}
                                            <fa-icon :icon="['fas', 'angle-down']" pull="right"/>
                                        </a>
                                    </div>
                                    <div id="additional_equipment" class="collapse show pt-3" aria-labelledby="headingL-varustus" data-parent="#ad_eqs">
                                        <app-loader-gif v-if="loading"/>
                                        <template v-else>
                                            <app-custom-checkbox v-for="(eq, j) in additions"
                                                                 :id="`eq_${j}`"
                                                                 :name="`eq_${j}`"
                                                                 :key="j"
                                                                 :value="form.additional_eq.map(e => e.id).includes(eq.id)"
                                                                 @input="addAddEq(eq)">
                                                {{ $t(eq.name[locale]) }}
                                                <small v-if="eq.all_prices && eq.all_prices.price">
                                                    ({{ ((eq.all_prices.price + eq.all_prices.added_price) / 100).toFixed(2) }} EUR)
                                                </small>
                                            </app-custom-checkbox>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <div class="accordion" id="services_acc">
                                <div class="card shadow-none">
                                    <div class="card-header pointer accordeon-border" data-toggle="collapse" href="#services">
                                        <a class="card-link font-weight-normal pl-1">
                                            {{ $t('Services') }}
                                            <fa-icon :icon="['fas', 'angle-down']" pull="right"/>
                                        </a>
                                    </div>
                                    <div id="services" class="collapse show" aria-labelledby="headingL-varustus" data-parent="#services_acc">
                                        <div class="card-body">
                                            <template v-if="form.services && form.services.length">
                                                <template v-for="(serv, s) in form.services">
                                                    <div class="d-flex justify-content-between" :key="`s_${s}`">
                                                        <p class="mb-1">
                                                            {{ serv.name[locale] }}, <strong>{{ (serv.price / 100).toFixed(2) }}€</strong>
                                                        </p>
                                                        <app-button v-if="form.edit_price" native-type="button" type="danger" @click="removeService(s)" xsmall>
                                                            {{ $t('Delete') }}
                                                        </app-button>
                                                    </div>
                                                </template>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col card p-3 mx-2">
                    <h4>{{ $t('Rentee') }}</h4>
                    <div class="row">
                        <div class="col-md-6">
                            <app-input id="firstName" v-model="form.fname" name="firstName" rules="required">
                                <template #label> {{ $t('First name') }}* </template>
                            </app-input>
                        </div>
                        <div class="col-md-6">
                            <app-input id="lastName" v-model="form.lname" name="lastName" rules="required">
                                <template #label> {{ $t('Last name') }}* </template>
                            </app-input>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <app-input id="phone" type="tel" v-model="form.phone" name="phone" @blur="checkClientDiscount" rules="required">
                                <template #label> {{ $t('Phone') }}* </template>
                            </app-input>
                        </div>
                        <div class="col-md-6">
                            <app-input id="email" v-model="form.email" name="email" @blur="checkClientDiscount">
                                <template #label> {{ $t('Email') }} </template>
                            </app-input>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-lg-6 col-md-6">
                            <app-textarea id="comments" v-model="form.comments" name="comments" :rows="3">
                                <template #label>
                                    {{ $t('Pick up & return info') }}
                                </template>
                            </app-textarea>
                        </div>
                        <div class="col-lg-6 col-md-6">
                            <app-select
                                id="lang"
                                v-model="form.lang"
                                :options="langs.map(l => l.key)"
                                name="lang"
                                openDirection="bottom"
                                rules="required"
                                class="mb-3"
                                searchable
                                not-empty
                                closeOnSelect
                                :custom-label="
                                    key =>
                                        langs.some(l => l.key === key)
                                            ? $t(langs.find(l => l.key === key).name)
                                            : null
                                ">
                                <template #label>
                                    {{ $t('Language of communication') }}*
                                </template>
                            </app-select>
                            <div class="mt-2 d-flex justify-content-start">
                                <app-custom-checkbox id="newsletter" v-model="form.newsletter" name="newsletter">
                                    {{ $t('Newsletter') }}
                                </app-custom-checkbox>
                            </div>
                        </div>
                    </div>
                    <hr>
                    <div class="d-flex justify-content-between">
                        <h4>{{ $t('Rental prices') }}</h4>
                        <div>
                            <app-radio
                                id="edit_price_no"
                                v-model="form.edit_price"
                                name="edit_price_no"
                                :label="$t('Automatically calculated price')"
                                :val="false"
                                inline />
                            <app-radio
                                id="edit_price_yes"
                                v-model="form.edit_price"
                                name="edit_price_yes"
                                :label="$t('Manually added price')"
                                :val="true"
                                inline />
                        </div>
                    </div>
                    <div class="row">
                        <div v-if="!form.edit_price" class="col">
                            <div class="row">
                                <div class="col">
                                    <label>{{ $t('Days') }}</label>
                                    <p class="custom-gray-input-bg">{{ form.prices.days }}</p>
                                </div>
                                <div class="col">
                                    <label>
                                        {{ $t('Day price') }}
                                    </label>
                                    <p class="custom-gray-input-bg">{{ form.prices.avg_price }} €</p>
                                </div>
                                <div class="col">
                                    <label>{{ $t('Eqipment price') }}</label>
                                    <p class="custom-gray-input-bg">{{ form.prices.eq_total }} €</p>
                                </div>
                                <div class="col">
                                    <label>{{ $t('Services') }}</label>
                                    <p class="custom-gray-input-bg">{{ form.prices.service_total }} €</p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col">
                                    <label>{{ $t('Extra hours') }}</label>
                                    <p class="custom-gray-input-bg">{{ form.prices.over_hrs }}</p>
                                </div>
                                <div class="col">
                                    <label>{{ $t('Extra hour price') }}</label>
                                    <p class="custom-gray-input-bg">{{ form.prices.hr_price }} €</p>
                                </div>
                                <div class="col">
                                    <label>{{ $t('Bond') }}</label>
                                    <p class="custom-gray-input-bg">{{ form.prices.bond }} €</p>
                                </div>
                                <div class="col">
                                    <label>{{ $t('Total price') }}</label>
                                    <p class="custom-gray-input-bg">{{ form.prices.total }} €</p>
                                </div>
                            </div>
                        </div>
                        <div v-else class="col">
                            <div class="row">
                                <div class="col">
                                    <app-input id="days" v-model="form.prices.days" name="days" rules="required" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Days') }} *</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="avg_price" v-model="form.prices.avg_price" name="avg_price" rules="required" type="number" :step="'0.01'">
                                        <template #label>
                                            {{ $t('Day price') }} *
                                        </template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="eq_total" v-model="form.prices.eq_total" name="eq_total" rules="required" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Eqipment price') }} *</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="service_total" v-model="form.prices.service_total" name="service_total" rules="required" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Services') }} *</template>
                                    </app-input>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col">
                                    <app-input id="over_hrs" v-model="form.prices.over_hrs" name="over_hrs" type="number">
                                        <template #label>{{ $t('Extra hours') }}</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="hr_price" v-model="form.prices.hr_price" name="hr_price" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Extra hour price') }}</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="bond" v-model="form.prices.bond" name="bond" rules="required" type="number" :step="'0.01'">
                                        <template #label>{{ $t('Bond') }} *</template>
                                    </app-input>
                                </div>
                                <div class="col">
                                    <app-input id="total" v-model="form.prices.total" name="total" rules="required" type="number" :step="'0.01'" disabled>
                                        <template #label>{{ $t('Total price') }}</template>
                                    </app-input>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p v-if="form.edit_price" class="text-danger text-center">
                        {{ $t('Discounts and promos will NOT be subtracted') }}
                    </p>
                    <div v-if="!form.edit_price" class="row mb-1">
                        <div class="col">
                            <template v-if="form.promocode_id">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Promocode') }}</label>
                                    <p class="custom-gray-input-bg">-{{ discount }}%</p>
                                </div>
                            </template>
                            <template v-else-if="form.client_id">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Client') }}</label>
                                    <p class="custom-gray-input-bg">-{{ discount }}%</p>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="form-group">
                        <app-button :loading="loading" small>
                            {{ $t('Save') }}
                        </app-button>
                    </div>
                </div>
            </div>
        </form>
    </validation-observer>
    <app-loader-svg v-else/>
</template>

<script>
import BookingService from '@services/booking.service';
import {mapGetters} from 'vuex';
import LocationService from '@services/location.service';
import VehicleService from '@services/vehicle.service';
import PublicBookingService from '@services/publicBooking.service';
import PublicSettingsService from '@services/publicSettings.service';

export default {
    data() {
        return {
            search_dates: [],
            start_time: '',
            end_time: '',
            form: {
                client_id: null,
                vehicle_id: null,
                prices: {},
                added_price: 0,
                edit_price: false,
                tos: true,
                newsletter: false,
                start_date: null,
                travel_out: null,
                end_date: null,
                start_time: null,
                end_time: null,
                promo: null,
                send_email: false,
                pickup_location_custom: '',
                return_location_custom: '',
                services: [],
                additional_eq: [],
            },
            vehicles: [],
            vehicle: {
                overlapping: {
                    start: null,
                    end: null
                }
            },
            additions: [],
            locations: [],
            promocode: null,
            loading: false,
            travel_out_check: false,
            discount: 0
        };
    },
    computed: {
        ...mapGetters(['locale', 'langs', 'otherLocationId'])
    },
    methods: {
        removeService(index) {
            this.form.services.splice(index, 1);
        },
        checkIsVehicleBooked() {
            if (!this.start_time || !this.end_time || !this.search_dates || !this.form.vehicle_id) {
                return false;
            }
            const queryObj = {
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                start_time: this.start_time,
                end_time: this.end_time,
                vehicle_id: this.form.vehicle_id,
            };

            PublicBookingService.checkIsVehicleBooked(queryObj);
        },
        checkPaidServices() {
            this.checkIsVehicleBooked();
            if (!this.start_time || !this.end_time || !this.search_dates || !this.form.pickup_location_id || !this.form.return_location_id || !this.form.vehicle_id) {
                return false;
            }
            if (this.form.edit_price) {
                return false;
            }
            const queryObj = {
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                start_time: this.start_time,
                end_time: this.end_time,
                pickup_location_id: this.form.pickup_location_id,
                pickup_custom: this.form.pickup_location_id === this.otherLocationId ? 1 : 0,
                return_location_id: this.form.return_location_id,
                return_custom: this.form.return_location_id === this.otherLocationId ? 1 : 0,
            };

            PublicBookingService.checkPaidServices(this.form.vehicle_id, queryObj).then(res => {
                this.form.services = res;
                this.getPrice(this.form.vehicle_id, false);
            });
        },
        checkClientDiscount() {
            const queryObj = {
                phone: this.form.phone,
                email: this.form.email
            };
            PublicBookingService.checkClientDiscount(queryObj).then(res => {
                if (res) {
                    this.discount = res.discount;
                    this.form.client_id = res.id;
                    this.form.promocode_id = null;
                } else {
                    this.discount = 0;
                    this.form.client_id = null;
                    this.checkPromo();
                }
            });
        },
        checkPromo() {
            if (!this.search_dates.length) {
                return false;
            }
            const queryObj = {
                code: this.promocode,
                start_date: this.search_dates[0],
                end_date: this.search_dates[1]
            };
            PublicBookingService.checkPromocode(queryObj).then(res => {
                if (res) {
                    this.discount = res.discount;
                    this.form.promocode_id = res.id;
                    this.form.client_id = null;
                } else {
                    this.discount = 0;
                    this.form.promocode_id = null;
                }
            });
        },
        getPrice(vehicleId, checkService = true) {
            if (!this.form.vehicle_id) {
                return false;
            }
            if (!this.search_dates.length) {
                return false;
            }
            const vehicle = this.vehicles.find(veh => veh.id === vehicleId);
            if (!vehicle) {
                return false;
            }
            const queryObj = {
                vehicle_id: vehicle.id,
                slug: vehicle.slug,
                discount: this.discount,
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                additional_eq: this.form.additional_eq,
                pickup_location_id: this.form.pickup_location_id,
                pickup_custom: this.form.pickup_location_id === this.otherLocationId ? 1 : 0,
                return_location_id: this.form.return_location_id,
                return_custom: this.form.return_location_id === this.otherLocationId ? 1 : 0,
            };
            if(this.start_time) {
                queryObj.start_time = this.start_time;
            }
            if(this.end_time) {
                queryObj.end_time = this.end_time;
            }

            this.getVehicle(queryObj);
            this.getAddEqPrices(queryObj);
            if (checkService) {
                this.checkPaidServices();
            }
        },
        getVehicle(queryObj) {
            PublicBookingService.getVehicle(queryObj).then(res => {
                this.vehicle = res.vehicle;
                if (!this.form.edit_price) {
                    this.form.services = res.vehicle.services;
                }
                if (!this.form.edit_price) {
                    this.setPrices(res.vehicle.all_prices);
                }
                this.checkIsVehicleBooked();
            });
        },
        setPrices(prices) {
            this.form.prices.eq_total = (prices.eq_total / 100).toFixed(2);
            this.form.prices.service_total = (prices.service_total / 100).toFixed(2);
            this.form.prices.avg_price = (prices.avg_price / 100).toFixed(2);
            this.form.prices.days = prices.days;
            this.form.prices.bond = prices.bond / 100;
            this.form.prices.total = (prices.total / 100).toFixed(2);
            this.form.prices.added_price = (prices.added_price / 100).toFixed(2);
            this.form.prices.hr_price = (prices.hr_price / 100).toFixed(2);
            this.form.prices.over_hrs = prices.over_hrs;
        },
        addAddEq(eq) {
            const ids = this.form.additional_eq.map(e => e.id);
            const index = ids.indexOf(eq.id);
            if (index > -1) {
                this.form.additional_eq.splice(index, 1);
            } else {
                this.form.additional_eq.push(eq);
            }
        },
        getVehicleName(id) {
            const vehicle = this.vehicles.some(c => c.id === id) ? this.vehicles.find(c => c.id === id) : null;
            if (vehicle) {
                return `${vehicle.make.name} ${vehicle.model.name} ${vehicle.slug.slice(-1)}, ${vehicle.reg_no}`;
            }
            return vehicle;
        },
        getVehicles() {
            VehicleService.noEmpties().then(res => {
                this.vehicles = res;
                this.form.vehicle_id = res[0].id;
                this.form.lang = this.locale;
                if (this.$route.query.vehicle) {
                    this.form.vehicle_id = this.$route.query.vehicle;
                }
            });
        },
        getLocations() {
            LocationService.locations().then(res => this.locations = res);
        },
        store(validate) {
            validate().then(valid => {
                if (valid) {
                    this.loading = true;
                    this.form.start_date = this.search_dates[0];
                    this.form.end_date = this.search_dates[1];
                    this.form.start_time = this.start_time;
                    this.form.end_time = this.end_time;
                    this.form.pickup_custom = this.form.pickup_location_id === this.otherLocationId ? 1 : 0;
                    this.form.return_custom = this.form.return_location_id === this.otherLocationId ? 1 : 0;

                    if (this.form.edit_price) {
                        this.setManualPrices();
                    }

                    BookingService.store(this.form).then(res => {
                        this.$toastr.s(this.$t('Saved'));
                        this.loading = false;
                        this.$router.push({name: 'booking.show', params: {booking_id: res.id}});
                    }).catch(e => this.loading = false);
                }
            });
        },
        setManualPrices() {
            this.form.prices.eq_total = this.form.prices.eq_total * 100;
            this.form.prices.service_total = this.form.prices.service_total * 100;
            this.form.prices.avg_price = this.form.prices.avg_price * 100;
            this.form.prices.bond = this.form.prices.bond * 100;
            this.form.prices.total = this.form.prices.total * 100;
            this.form.prices.added_price = this.form.prices.added_price * 100;
            this.form.prices.hr_price = this.form.prices.hr_price * 100;
        },
        getAddEqPrices(queryObj) {
            PublicSettingsService.addEq(queryObj).then(res => {
                this.additions = res;
            });
        },
    },
    created() {
        this.getLocations();
        this.getVehicles();
    }
};
</script>
